import { useState, useCallback, useEffect, useRef } from 'react';
import { generateProductVideo, pollVideoStatus } from '../services/geminiService';

const VEO_LOADING_MESSAGES = [
  "Warming up the virtual studio...",
  "Directing the digital lighting...",
  "Polishing the product pixels...",
  "Rendering the main sequence...",
  "Adding the final marketing magic...",
  "Almost there, preparing your video...",
];

export const useVeo = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const messageIntervalRef = useRef(null);

  const cleanupIntervals = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanupIntervals();
  }, []);

  const generateVideo = useCallback(async (uploadedFile, aspectRatio, prompt) => {
    setIsGenerating(true);
    setVideoUrl(null);
    setError(null);
    setStatusMessage("Starting video generation...");

    let messageIndex = 0;
    messageIntervalRef.current = window.setInterval(() => {
      const messages = [
        "Warming up the virtual studio...",
        "Directing the digital lighting...",
        "Polishing the product pixels...",
        "Rendering the main sequence...",
        "Adding the final marketing magic...",
        "Almost there, preparing your video...",
      ];
      setStatusMessage(messages[messageIndex % messages.length]);
      messageIndex++;
    }, 5000);

    try {
      let operation = await generateProductVideo(uploadedFile, aspectRatio, prompt);
      
      const pollInterval = window.setInterval(async () => {
        try {
          const status = await pollVideoStatus(operation);
          
          if (status.done) {
            cleanupIntervals();
            clearInterval(pollInterval);
            
            if (status.videoUrl) {
              setStatusMessage("Video ready!");
              setVideoUrl(status.videoUrl);
            } else {
              throw new Error("Video generation completed but no video URL was found.");
            }
            
            setIsGenerating(false);
          }
        } catch(pollError) {
          cleanupIntervals();
          clearInterval(pollInterval);
          setIsGenerating(false);
          setError(pollError.message);
          console.error("Polling error:", pollError);
        }
      }, 5000);
      
      pollingIntervalRef.current = pollInterval;
      
    } catch (err) {
      cleanupIntervals();
      setIsGenerating(false);
      setError(err.message);
      console.error(err);
    }
  }, []);

  return {
    generateVideo,
    isGenerating,
    videoUrl,
    statusMessage,
    error,
    promptForApiKey: false,
    selectApiKey: () => {},
  };
};